type Listener<TArgs extends unknown[]> = (...args: TArgs) => void;

/**
 * Minimal synchronous event emitter covering the surface the realtime pod needs:
 * `on`, `addListener`, `removeListener`, `removeAllListeners`, `listeners` and
 * `emit`.
 *
 * It replaces Node's `EventEmitter` (via the `events` npm shim), which pulled a
 * ~15KB CJS module into every browser bundle that reaches this pod and had to be
 * special-cased in the Storybook vite resolvers because `require.resolve("events")`
 * returns a Node builtin id.
 *
 * Semantics deliberately match `EventEmitter` for the parts this pod relies on:
 * listeners fire synchronously, in registration order, and `emit` has run every
 * listener before it returns. `EventTarget` was not used because it would force
 * every payload through `CustomEvent` and offers no equivalent of `listeners()`,
 * which the iframe adapter uses to inspect registrations.
 *
 * One deliberate departure: listeners are called unbound, where `EventEmitter`
 * invoked them with the emitter as `this`. Every listener in this pod is an arrow
 * function or is bound at registration, and a `function` listener that reached for
 * `this` would throw in a module's strict mode rather than fail quietly.
 *
 * There is no `once`: nothing in the pod needs one-shot delivery. A caller that
 * wants it removes its own listener from inside the listener.
 *
 * @typeParam TArgs - the argument tuple every event on this emitter carries, which
 *   types both the listeners it accepts and what `emit` requires.
 */
export default class Emitter<TArgs extends unknown[] = unknown[]> {
  // An array per event, not a Set: registration order is part of the contract and
  // EventEmitter permits the same listener to be registered more than once.
  private _listeners = new Map<string, Listener<TArgs>[]>();

  private _register(event: string, listener: Listener<TArgs>): this {
    const listeners = this._listeners.get(event);
    if (listeners) {
      listeners.push(listener);
    } else {
      this._listeners.set(event, [listener]);
    }
    return this;
  }

  addListener(event: string, listener: Listener<TArgs>): this {
    return this._register(event, listener);
  }

  on(event: string, listener: Listener<TArgs>): this {
    return this._register(event, listener);
  }

  // Removes one registration, matching EventEmitter: a listener registered twice
  // needs removing twice.
  removeListener(event: string, listener: Listener<TArgs>): this {
    const listeners = this._listeners.get(event);
    if (!listeners) return this;
    const index = listeners.indexOf(listener);
    if (index !== -1) listeners.splice(index, 1);
    if (listeners.length === 0) this._listeners.delete(event);
    return this;
  }

  removeAllListeners(event?: string): this {
    if (event === undefined) {
      this._listeners.clear();
    } else {
      this._listeners.delete(event);
    }
    return this;
  }

  listeners(event: string): Listener<TArgs>[] {
    return [...(this._listeners.get(event) ?? [])];
  }

  emit(event: string, ...args: TArgs): boolean {
    const listeners = this._listeners.get(event);
    if (!listeners || listeners.length === 0) return false;
    // Dispatch from a copy: a listener that subscribes or unsubscribes during
    // dispatch must not change who receives this emit.
    for (const listener of [...listeners]) {
      listener(...args);
    }
    return true;
  }
}
